(function () {
  const SECTION_SELECTOR = ".growave-reviews";
  const BREAKPOINTS = "default:3,<768:1";
  const MOBILE_QUERY = "(max-width: 767px)";

  const CARD_SELECTORS = [
    '[class*="gw-rv-slider-item_card"]',
    '[class*="gw-rv-slider-item__card"]',
    '[class*="gw-rv-slider-item__image-wrapper"]',
    '[class*="gw-rv-slider-item__content"]',
    '[class*="gw-rv-main-slider-widget-wrapper"]',
    '[class*="gw-rv-main-slider"]',
  ];

  const RADIUS_PROPS = [
    "border-radius",
    "border-top-left-radius",
    "border-top-right-radius",
    "border-bottom-left-radius",
    "border-bottom-right-radius",
  ];

  const RADIUS_VARS = [
    "--gw-border-radius-300",
    "--gw-border-radius-200",
    "--gw-border-radius-100",
    "--gw-p-border-radius-card",
  ];

  function collectRoots(root) {
    const roots = [root];
    root.querySelectorAll("*").forEach((el) => {
      if (el.shadowRoot) roots.push(el.shadowRoot);
    });
    return roots;
  }

  function setSquareVars(el) {
    RADIUS_VARS.forEach((name) => {
      el.style.setProperty(name, "0px", "important");
    });
  }

  function configureBreakpoints(section) {
    section
      .querySelectorAll(
        '.gw-rv-reviews-slider-widget-placeholder, [class*="gw-rv-reviews-slider"]'
      )
      .forEach((el) => {
        if (!el.dataset.gwSliderBreakpoints) {
          el.dataset.gwSliderBreakpoints = BREAKPOINTS;
        }
        if (!el.dataset.gwBreakpoints) {
          el.dataset.gwBreakpoints = BREAKPOINTS;
        }
      });
  }

  function flattenCards(section) {
    collectRoots(section).forEach((root) => {
      CARD_SELECTORS.forEach((selector) => {
        root.querySelectorAll(selector).forEach((el) => {
          setSquareVars(el);
          RADIUS_PROPS.forEach((prop) => {
            el.style.setProperty(prop, "0", "important");
          });
        });
      });

      root.querySelectorAll("*").forEach((el) => {
        if (el.shadowRoot) setSquareVars(el);
      });
    });
  }

  function getSliderElements(section) {
    let track = null;
    let slider = null;
    let items = [];

    collectRoots(section).forEach((root) => {
      if (!track) {
        track = root.querySelector('[class*="gw-slider__track"]');
      }
      if (!slider) {
        slider = root.querySelector('[class*="gw-slider"]:not([class*="gw-slider__"])');
      }
      if (!slider) {
        slider = root.querySelector('[class*="gw-slider"]');
      }
    });

    if (track) {
      items = [...track.children].filter((el) => {
        const cls = el.className || "";
        return typeof cls === "string" && cls.includes("gw-rv-slider-item");
      });
    }

    return { track, slider, items };
  }

  function parseTranslateX(el) {
    const inline = el.style.transform || "";
    const inlineMatch = inline.match(/translate3d\((-?\d+\.?\d*)px/);
    if (inlineMatch) return parseFloat(inlineMatch[1]);

    const computed = getComputedStyle(el).transform;
    if (!computed || computed === "none") return 0;

    if (computed.startsWith("matrix3d")) {
      const values = computed
        .match(/matrix3d\(([^)]+)\)/)[1]
        .split(",")
        .map((v) => parseFloat(v.trim()));
      return values[12] || 0;
    }

    if (computed.startsWith("matrix")) {
      const values = computed
        .match(/matrix\(([^)]+)\)/)[1]
        .split(",")
        .map((v) => parseFloat(v.trim()));
      return values[4] || 0;
    }

    return 0;
  }

  function getPerView(slider) {
    if (!slider) return 1;

    const cssVar = getComputedStyle(slider).getPropertyValue("--per-page").trim();
    const parsed = parseFloat(cssVar);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;

    return window.matchMedia(MOBILE_QUERY).matches ? 1 : 3;
  }

  function getSlideOffsets(items) {
    return items.map((item) => item.offsetLeft);
  }

  function getNearestIndex(track, offsets) {
    const currentX = Math.max(0, -parseTranslateX(track));
    let nearest = 0;
    let smallest = Infinity;

    offsets.forEach((offset, index) => {
      const diff = Math.abs(offset - currentX);
      if (diff < smallest) {
        smallest = diff;
        nearest = index;
      }
    });

    return nearest;
  }

  function getMaxIndex(items, perView) {
    return Math.max(0, items.length - Math.ceil(perView));
  }

  function snapTrackToIndex(track, offsets, index, animate) {
    const target = offsets[index] || 0;
    track.style.transition = animate ? "transform 0.45s ease" : "none";
    track.style.transform = `translate3d(${-target}px, 0px, 0px)`;
  }

  function moveSlider(section, direction) {
    const { track, slider, items } = getSliderElements(section);
    if (!track || !slider || items.length === 0) return;

    const offsets = getSlideOffsets(items);
    if (offsets.length === 0) return;

    const perView = getPerView(slider);
    const maxIndex = getMaxIndex(items, perView);
    const currentIndex = getNearestIndex(track, offsets);
    const nextIndex = Math.min(Math.max(currentIndex + direction, 0), maxIndex);

    snapTrackToIndex(track, offsets, nextIndex, true);
  }

  function syncTrackPosition(section, animate) {
    const { track, slider, items } = getSliderElements(section);
    if (!track || !slider || items.length === 0) return;

    const offsets = getSlideOffsets(items);
    if (offsets.length === 0) return;

    const perView = getPerView(slider);
    const maxIndex = getMaxIndex(items, perView);
    const currentIndex = Math.min(getNearestIndex(track, offsets), maxIndex);

    snapTrackToIndex(track, offsets, currentIndex, animate);
  }

  function hideNativeNav(section) {
    collectRoots(section).forEach((root) => {
      root.querySelectorAll("button").forEach((button) => {
        if (button.classList.contains("growave-reviews__arrow")) return;
        if (!button.closest('[class*="gw-rv-slider-item"]')) {
          const cls = button.className || "";
          const label = (button.getAttribute("aria-label") || "").toLowerCase();
          const combined = `${cls} ${label}`;
          if (/arrow|prev|next|previous|navigation|nav|前|次|戻|進/.test(combined)) {
            button.classList.add("growave-reviews__native-nav");
          }
        }
      });
    });
  }

  function wireArrows(section) {
    const themePrev = section.querySelector(".growave-reviews__arrow--prev");
    const themeNext = section.querySelector(".growave-reviews__arrow--next");

    if (themePrev && !themePrev.dataset.wired) {
      themePrev.addEventListener("click", (event) => {
        event.preventDefault();
        moveSlider(section, -1);
      });
      themePrev.dataset.wired = "true";
    }

    if (themeNext && !themeNext.dataset.wired) {
      themeNext.addEventListener("click", (event) => {
        event.preventDefault();
        moveSlider(section, 1);
      });
      themeNext.dataset.wired = "true";
    }
  }

  function apply(section) {
    configureBreakpoints(section);
    flattenCards(section);
    hideNativeNav(section);
    wireArrows(section);
  }

  function init() {
    const section = document.querySelector(SECTION_SELECTOR);
    if (!section) return;

    configureBreakpoints(section);
    apply(section);

    let timer;
    const observer = new MutationObserver(() => {
      clearTimeout(timer);
      timer = window.setTimeout(() => {
        apply(section);
        syncTrackPosition(section, false);
      }, 150);
    });

    observer.observe(section, { childList: true, subtree: true });

    window.addEventListener("resize", () => {
      syncTrackPosition(section, false);
    });

    window.setTimeout(() => observer.disconnect(), 30000);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
